import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

// The 5 in-scope store types
export const STORE_TYPES = {
  phone_numbers: 'phone_numbers',
  car_plates:    'car_plates',
  auto:          'auto',
  real_estate:   'real_estate',
  ecommerce:     'ecommerce',
};

// Customer backend proxy — reads directly from cloudgm_business.store_items
// (bypassing the slow cloudgm.business Merchant HTTP API which returns 40MB
// base64 payloads). Response < 100KB per store type in ~0.4s.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useMerchantMarket — fast active-listings feed for a given store_type.
 * Items are pre-enriched with their store info (contact_phone/whatsapp/email +
 * store_uid + logo). Archived items are filtered out server-side.
 */
export function useMerchantMarket(storeType, opts = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const limit = opts.limit || 300;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/customer/marketplace/list`,
        { params: { type: storeType, limit }, timeout: 15000 }
      );
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load market data');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [storeType, limit]);

  useEffect(() => { load(); }, [load]);

  return { items, storeMap: {}, loading, error, refresh: load };
}

/**
 * Fetch a single marketplace item by id. Optimized single-doc query on the
 * customer backend (~0.1s response, no base64 in payload).
 * @param {string} _storeType — kept for API compat, unused now
 * @param {string} id
 * @param {object|null} cachedStore — optional store data from router state
 */
export async function fetchMerchantItem(_storeType, id, cachedStore = null) {
  const { data } = await axios.get(
    `${BACKEND_URL}/api/customer/marketplace/item/${id}`,
    { timeout: 15000 }
  );
  const item = data?.item || {};
  const store = { ...(item.store || {}), ...(cachedStore || {}) };
  return { item, store };
}
